import { Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * Countdown badge for the deadline by which a subcontractor must submit their quote.
 * Red if <48h, amber if <7 days, emerald if >7 days, grey if past.
 *
 * Props:
 *  - deadline: ISO date string (yyyy-mm-dd) or null
 *  - lang: 'fr' | 'en'
 *  - dense: optional bool for compact display
 */
export default function QuoteDeadlineBadge({ deadline, lang = 'fr', dense = false }) {
  if (!deadline) return null;
  const dt = new Date(deadline);
  if (isNaN(dt.getTime())) return null;
  const now = new Date();
  // Compare end-of-day for the deadline (the sub has the whole day to submit)
  const endOfDay = new Date(dt); endOfDay.setHours(23, 59, 59, 999);
  const diffMs = endOfDay.getTime() - now.getTime();
  const totalHours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  let tone, label, Icon;
  if (diffMs < 0) {
    tone = 'past'; Icon = AlertTriangle;
    label = lang === 'fr' ? 'Délai dépassé' : 'Past due';
  } else if (totalHours < 48) {
    tone = 'urgent'; Icon = AlertTriangle;
    label = totalHours < 24
      ? (lang === 'fr' ? `À rendre aujourd'hui (${totalHours}h)` : `Due today (${totalHours}h)`)
      : (lang === 'fr' ? `Plus que ${totalHours}h pour répondre` : `${totalHours}h left to reply`);
  } else if (days < 7) {
    tone = 'soon'; Icon = Clock;
    label = lang === 'fr'
      ? `Plus que ${days} jour${days > 1 ? 's' : ''} pour répondre`
      : `${days} day${days !== 1 ? 's' : ''} left to reply`;
  } else {
    tone = 'ok'; Icon = CheckCircle2;
    const fmt = dt.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US',
      { day: '2-digit', month: 'short' });
    label = lang === 'fr' ? `Réponse avant le ${fmt}` : `Reply by ${fmt}`;
  }
  const cls = {
    urgent: 'bg-rose-500/15 text-rose-300 border-rose-500/40 animate-pulse',
    soon:   'bg-amber-500/15 text-amber-300 border-amber-500/40',
    ok:     'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    past:   'bg-zinc-700/40 text-zinc-400 border-zinc-600/40',
  }[tone];
  return (
    <span
      className={`inline-flex items-center gap-1 ${dense ? 'text-[10px] px-1.5 py-0.5' : 'text-[11px] px-2 py-1'} rounded-sm border font-medium ${cls}`}
      data-testid={`quote-deadline-badge-${tone}`}
      title={label}
    >
      <Icon className={dense ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      {label}
    </span>
  );
}
